import * as React from 'react';
import { Form } from 'react-bootstrap';
import { ChecksGroup, Dropdown, Editable, useCacheQuery } from 'gossamer-front-end';
import { Location } from 'cb-model';
import { EventDetailsSectionLayout } from './EventDetailsSectionLayout';
import { EventDetails, EventErrors, HandleEventDetailsChange, ValidateEventFormField } from './eventDetailsFormTypes';
import { FieldType } from 'gossamer-unified';
import { useEventDateTimeFields } from './useEventDateTimeFields';
import { EventRepetitionSection } from './EventRepetitionSection';

type Props<T extends EventDetails> = {
  eventDetails: T | undefined;
  handleDetailsChange: HandleEventDetailsChange<T>;
  errors: EventErrors;
  setErrors: React.Dispatch<React.SetStateAction<EventErrors>>;
  institutionId: number;
  isUpdate?: boolean;
  lockDateTimes?: boolean;
  validateForm: ValidateEventFormField<T>;
};

export function EventHostingDetailsSection<T extends EventDetails>(props: Props<T>): React.JSX.Element {
  const { eventDetails, handleDetailsChange, errors, setErrors, institutionId, isUpdate, lockDateTimes, validateForm } =
    props;

  const locationQuery = useCacheQuery(
    Location,
    {
      where: {
        and: [{ fieldId: 'institution_id', operator: 'eq', value: institutionId }],
      },
      order: [['name', 'ASC']],
      limit: 250,
    },
    !institutionId,
  );

  const isAtPhysicalLocation =
    (eventDetails as any)?.location_type === 'in-person' || (eventDetails as any)?.location_type === 'hybrid';

  const isGpsTrackingAvailable =
    isAtPhysicalLocation &&
    (locationQuery.data?.length || 0) > 0 &&
    !!(eventDetails as any)?.flow_option &&
    ['attendanceOnly', 'full'].includes((eventDetails as any)?.flow_option);

  const isAtSpecificTime = !!(eventDetails as any)?.type && (eventDetails as any)?.type !== 'on-demand';

  const isLivestream =
    !!(eventDetails as any)?.location_type &&
    (eventDetails as any)?.location_type !== 'in-person' &&
    !!(eventDetails as any)?.type &&
    (eventDetails as any)?.type !== 'on-demand';

  const startDateTime = String((eventDetails as any)?.start_date || '');
  const endDateTime = String((eventDetails as any)?.end_date || '');
  const { datePart, startTime, endTime, endTimeError, handleDateChange, handleStartTimeChange, handleEndTimeChange } =
    useEventDateTimeFields({
      startDateTime,
      endDateTime,
      isAtSpecificTime,
      handleDetailsChange,
    });

  const locationTypeOptions = [
    { id: 'in-person', label: 'In person' },
    { id: 'online', label: 'Online' },
    { id: 'hybrid', label: 'Hybrid' },
  ];

  const LOCATION_FIELD = React.useMemo(
    () => ({ seq: 20, type: FieldType.Text, label: 'Event location', mandatory: true }),
    [],
  );

  const gpsOptions = (locationQuery.data || []).map((loc: any) => ({ id: String(loc.id), label: loc.name }));

  React.useEffect(() => {
    validateForm({ eventDetails, setErrors, key: 'start_date' as keyof T });
  }, [eventDetails?.type, (eventDetails as any)?.start_date, (eventDetails as any)?.end_date, setErrors, validateForm]);

  const dateTimeError = errors.start_date || errors.end_date;

  return (
    <EventDetailsSectionLayout
      title="Hosting details"
      subtitle="Where does this event take place and how will attendees find it?"
    >
      <Form.Group controlId="location-type">
        <Form.Label>Hosting method*</Form.Label>
        <ChecksGroup
          type="radio"
          inline={true}
          mandatory={true}
          options={locationTypeOptions}
          value={String((eventDetails as any)?.location_type || '')}
          onItemSelected={(value) => {
            handleDetailsChange('location_type' as keyof T, value);
            if (value === 'online') {
              handleDetailsChange('location' as keyof T, undefined);
              handleDetailsChange('gps_location' as keyof T, undefined);
            }
            validateForm({ eventDetails, setErrors, key: 'location_type' as keyof T });
          }}
        />
        {errors.location_type && <Form.Text className="text-danger">{errors.location_type}</Form.Text>}
      </Form.Group>

      {isAtPhysicalLocation && (
        <Form.Group controlId="location">
          <Form.Label>Event location*</Form.Label>
          <Editable
            field={LOCATION_FIELD as any}
            value={String((eventDetails as any)?.location || '')}
            placeholder="Queen Elizabeth Hospital, Room 101"
            validity={errors.location ? false : undefined}
            handleChange={(val) => handleDetailsChange('location' as keyof T, val)}
            handleBlur={() => validateForm({ eventDetails, setErrors, key: 'location' as keyof T })}
          />
          {errors.location && <Form.Text className="text-danger">{errors.location}</Form.Text>}
        </Form.Group>
      )}

      {isGpsTrackingAvailable && (
        <Form.Group controlId="gps-location">
          <Form.Label>GPS Location (to enable GPS-based attendance tracking)</Form.Label>
          <Dropdown
            mandatory={false}
            onItemSelected={(value) => {
              const parsed = value ? parseInt(value, 10) : NaN;
              handleDetailsChange('gps_location' as keyof T, Number.isFinite(parsed) ? parsed : undefined);
            }}
            options={gpsOptions}
            value={String((eventDetails as any)?.gps_location || '')}
          />
        </Form.Group>
      )}

      {isLivestream && (
        <div style={{ padding: 12, border: '1px solid #dee2e6', borderRadius: 6 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>You can livestream this event on Careerbook!</div>
          <div>
            You can start the livestream from the Event Details page, which you'll be redirected to after you finish
            creating the event.
          </div>
        </div>
      )}

      {isAtSpecificTime && !lockDateTimes && (
        <Form.Group controlId="datetime">
          <Form.Label>Event date and time</Form.Label>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '2 1 220px' }}>
              <Form.Label className="small">Event date</Form.Label>
              <Form.Control type="date" value={datePart} onChange={(e) => handleDateChange(e.currentTarget.value)} />
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <Form.Label className="small">Start time</Form.Label>
              <Form.Control
                type="time"
                value={startTime}
                onChange={(e) => handleStartTimeChange(e.currentTarget.value)}
              />
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <Form.Label className="small">End time</Form.Label>
              <Form.Control
                type="time"
                value={endTime}
                onChange={(e) => handleEndTimeChange(e.currentTarget.value)}
                isInvalid={!!endTimeError}
              />
              {endTimeError && <Form.Text className="text-danger">{endTimeError}</Form.Text>}
            </div>
          </div>
          {!!dateTimeError && <Form.Text className="text-danger">{dateTimeError}</Form.Text>}
        </Form.Group>
      )}

      {isAtSpecificTime && !!lockDateTimes && (
        <div style={{ padding: 12, border: '1px solid #dee2e6', borderRadius: 6 }}>
          Event date and time cannot be changed once paid tickets exist.
        </div>
      )}

      {isAtSpecificTime && eventDetails && (
        <EventRepetitionSection
          value={eventDetails}
          isUpdate={isUpdate}
          onChange={(repetition) => handleDetailsChange('repetition' as keyof T, repetition)}
        />
      )}
    </EventDetailsSectionLayout>
  );
}
